import { useCallback, useEffect, useState } from "react";
import { http } from "../lib/api";

const DAY_MS = 864e5;

function deriveGateStats(blocks, lockouts) {
  const dayAgo = Date.now() - DAY_MS;
  const gateBlocks = blocks.filter(
    (b) => b.action_type?.startsWith("TERMINAL_GATE") && new Date(b.created_at).getTime() >= dayAgo
  );
  return {
    access: gateBlocks.filter((b) => b.action_type === "TERMINAL_GATE_UNLOCKED").length,
    blocked: gateBlocks.filter((b) => b.action_type === "TERMINAL_GATE_ACCESS_DENIED").length,
    lockedIPs: (lockouts?.locked || []).map((l) => l.ip),
  };
}

// Every piece of server state the console polls, in one place.
export function useConsoleData(active, screen) {
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [pending, setPending] = useState([]);
  const [logs, setLogs] = useState([]);
  const [sources, setSources] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [matters, setMatters] = useState([]);
  const [matterId, setMatterId] = useState("");
  const [matterDetail, setMatterDetail] = useState(null);
  const [matterLoading, setMatterLoading] = useState(false);
  const [drills, setDrills] = useState([]);
  const [health, setHealth] = useState(null);
  const [gateStats, setGateStats] = useState({ access: 0, blocked: 0, lockedIPs: [] });

  const refresh = useCallback(async () => {
    const get = (p) => http.get(p).then((r) => r.data).catch(() => null);
    const [st, sm, pd, lg, sr, ev, bl, ex, mt, hl, dr, lk] = await Promise.all([
      get("/status"), get("/executive/summary"), get("/pending-actions"), get("/audit-stream"),
      get("/sources"), get("/evidence"), get("/worm/blocks"), get("/exceptions"),
      get("/matters"), get("/health"), get("/worm/drills"), get("/gate/lockouts"),
    ]);
    if (st) setStatus(st);
    if (sm) setSummary(sm);
    if (pd) setPending(pd);
    if (lg) setLogs(lg);
    if (sr) setSources(sr);
    if (ev) setEvidence(ev);
    if (bl) setBlocks(bl);
    if (ex) setExceptions(ex);
    if (hl) setHealth(hl);
    if (dr) setDrills(dr);
    if (mt) {
      setMatters(mt);
      setMatterId((cur) => cur || mt[0]?.matter_id || "");
    }
    if (bl) setGateStats(deriveGateStats(bl, lk));
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    refresh();
    const timer = setInterval(refresh, 7000);
    return () => clearInterval(timer);
  }, [active, refresh]);

  useEffect(() => {
    if (!active || !matterId) return;
    setMatterLoading(true);
    http
      .get(`/matters/${matterId}`)
      .then((r) => setMatterDetail(r.data))
      .catch(() => setMatterDetail(null))
      .finally(() => setMatterLoading(false));
  }, [active, matterId, screen, evidence.length, blocks.length]);

  return {
    status, setStatus, summary, pending, logs, sources, setSources, evidence, blocks, exceptions,
    matters, matterId, setMatterId, matterDetail, matterLoading, drills, health, gateStats,
    refresh,
  };
}
